"use client";

import { useCallback, type ReactElement } from "react";
import dynamic from "next/dynamic";
import { usePathname, useRouter } from "next/navigation";
import type { CsessionstartchooserProps } from "@/components/ui/Csessionstartchooser";
import { useHasOpened } from "@/app/home-page/useHasOpened";
import { useAuth } from "@/contexts/auth";
import { useAuthModal } from "@/contexts/layout";
import {
  TEAM_MODAL_CENTER_ORIGIN,
  requestNewTeamModal,
  useTeamModal,
} from "@/contexts/team-modal";
import { buildAllTeamsGalleryPath } from "@/hooks/navigation/all-teams-url";
import { HOME_ROUTE } from "@/lib/authReturnRoute";
import { useSessionStartChooser } from "./session-start-chooser-context";

const Csessionstartchooser = dynamic<CsessionstartchooserProps>(
  () =>
    import("@/components/ui/Csessionstartchooser").then(
      (module) => module.Csessionstartchooser,
    ),
  { ssr: false },
);

/**
 * The ONE mount of the session-start chooser overlay, driven by
 * `SessionStartChooserProvider`. Option semantics (ruled 2026-08-29):
 *
 * - "New team" — the build CTA's gated flow: logged out parks the new-team
 *   intent on the one post-login carrier and raises the auth gate; logged in
 *   opens the canonical team modal on an empty subject with edit invoked.
 * - "Existing team" — both named collections live in the All Teams gallery, so
 *   the option always lands there (ruled 2026-08-29). On the landing it calls
 *   the opener the landing lent this context, which IS the hero "All teams"
 *   CTA's handler; off the landing it navigates home carrying the one-shot
 *   gallery param, which the landing's `useAllTeamsGalleryUrlRestore` consumes
 *   on mount. Two arrivals, one opener.
 * - "Automatic team" — TBD flow; the component renders it muted, no callback.
 *
 * Opening the chooser itself takes NO auth gate — it is a menu; gating is per
 * option. The never-opened latch keeps the chunk unloaded until the first
 * open, then stays mounted so the exit fade can play.
 */
export function SessionStartChooserHost(): ReactElement | null {
  const {
    isSessionStartChooserOpen,
    closeSessionStartChooser,
    openAllTeamsGallery,
  } = useSessionStartChooser();
  const { data: authData } = useAuth();
  const isLoggedIn = authData?.user != null;
  const { openAuthModal } = useAuthModal();
  const { openNew } = useTeamModal();
  const router = useRouter();
  const pathname = usePathname();
  const hasOpened = useHasOpened(isSessionStartChooserOpen);

  // The chooser closes first and synchronously; the gate is then whatever the
  // shared build gate resolves to — signed out, only once the intent is parked.
  const handleNewTeam = useCallback(async (): Promise<void> => {
    closeSessionStartChooser();
    await requestNewTeamModal({
      isAuthenticated: isLoggedIn,
      openAuthModal,
      openNew,
      origin: TEAM_MODAL_CENTER_ORIGIN,
    });
  }, [closeSessionStartChooser, isLoggedIn, openAuthModal, openNew]);

  const handleExistingTeam = useCallback((): void => {
    closeSessionStartChooser();
    // The path decides which arrival this is; the lent opener exists exactly
    // while the landing is mounted, so the two conditions never disagree.
    if (pathname === HOME_ROUTE) {
      openAllTeamsGallery();
      return;
    }
    router.push(buildAllTeamsGalleryPath());
  }, [closeSessionStartChooser, openAllTeamsGallery, pathname, router]);

  if (!hasOpened) return null;

  return (
    <Csessionstartchooser
      visible={isSessionStartChooserOpen}
      onClose={closeSessionStartChooser}
      onNewTeam={handleNewTeam}
      onExistingTeam={handleExistingTeam}
    />
  );
}
